import { z } from 'zod';
import { metadataSchema } from '../common/metadata';

/**
 * Zod schemas for accounting-related entities
 * These provide runtime validation and type inference
 */

const intCents = z.coerce.number().int();

// Bill line item schema (integer cents only; strict)
const billLineItemSchema = z
  .object({
    description: z.string(),
    quantity: z.number().nonnegative(),
    unitPriceCents: intCents,
    totalCents: intCents,
  })
  .strict();

// Bill schema (API/Prisma: coerce dates, status, paymentMethod)
const billStatusEnum = ['Pending Approval', 'Approved', 'Paid', 'Overdue'] as const;
const billPaymentMethodEnum = ['Check', 'ACH', 'Credit Card', 'Cash', 'Money Order'] as const;
const dateLike = z.union([z.string(), z.date()]).transform(v => (v instanceof Date ? v.toISOString().split('T')[0] : String(v)));
const isoDateTimeLike = z
  .union([z.string(), z.date()])
  .transform(v => (v instanceof Date ? v.toISOString() : String(v)));

export const billSchema = z
  .object({
    id: z.string(),
    vendorId: z.string(),
    propertyId: z.string(),
    category: z.string().nullish().default(''),
    amountCents: intCents,
    date: dateLike,
    dueDate: dateLike,
    status: z
      .string()
      .transform(s =>
        billStatusEnum.includes(s as (typeof billStatusEnum)[number]) ? s : 'Pending Approval'
      ) as z.ZodType<(typeof billStatusEnum)[number]>,
    description: z.string().nullish(),
    paidDate: z
      .union([z.string(), z.date()])
      .optional()
      .nullable()
      .transform(v => (v == null ? undefined : v instanceof Date ? v.toISOString().split('T')[0] : String(v))),
    paymentMethod: z
      .string()
      .optional()
      .nullable()
      .transform(v => {
        if (v == null || v === '') return undefined;
        return billPaymentMethodEnum.includes(v as (typeof billPaymentMethodEnum)[number])
          ? (v as (typeof billPaymentMethodEnum)[number])
          : undefined;
      }),
    ticketId: z.string().nullish(),
    workflowTraceId: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    payee: z.string().nullish(),
    referenceId: z.string().nullish(),
    referenceType: z.string().nullish(),
    chartAccountId: z.string().nullish(),
    billDate: z.string().nullish(),
    purchaseOrderId: z.string().nullish(),
    lineItems: z.array(billLineItemSchema).nullish(),
    vendorName: z.string().optional(),
    propertyName: z.string().optional(),
    vendorCostCents: intCents.optional(),
    markupCents: intCents.optional(),
    ownerChargeCents: intCents.optional(),
    createdAt: isoDateTimeLike,
    updatedAt: isoDateTimeLike,
  })
  .strict();

// Export schema only, type is in transactions.ts

// Payment schema (strict: wire DTO is cents-only for principal; no legacy `amount` float)
export const paymentSchema = z
  .object({
    id: z.string(),
    transactionId: z.string(),
    stripePaymentIntentId: z.string().nullable().optional(),
    unallocatedAmountCents: z.number().int().nullable().optional(),
    paymentSource: z.enum(['STRIPE_CONNECT', 'SYNTHETIC_SEED', 'MANUAL', 'OTHER'] as const).optional(),
    idempotencyKey: z.string().optional(),
    amountCents: z.number().int(),
    method: z.enum(['Check', 'ACH', 'Credit Card', 'Cash', 'Money Order'] as const),
    status: z.enum(['Pending', 'Processing', 'Completed', 'Cleared', 'Paid', 'Failed', 'Refunded', 'Cancelled'] as const),
    tenantId: z.string().optional(),
    propertyId: z.string().optional(),
    description: z.string().optional(),
    receivableId: z.string().optional(),
    journalEntryId: z.string().optional(),
    processedAt: z.string().optional(),
    completedAt: z.string().optional(),
    failedAt: z.string().optional(),
    failureReason: z.string().optional(),
    refundedAmount: z.number().nonnegative().optional(),
    refundedAt: z.string().optional(),
    refundReason: z.string().optional(),
    metadata: metadataSchema.nullish(),
    /** Last Stripe (or gateway) webhook / confirmed sync — not memo edits (see docs/architecture/GATEWAY_FRESHNESS.md). */
    lastProviderWebhookAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    date: z.string().optional(),
    reference: z.string().optional(),
  })
  .strict();

// Export schema only, type is in transactions.ts

// BankAccount schema
export const bankAccountSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Bank account name is required'),
  balance: z.number(),
  type: z.enum(['Operating', 'Trust', 'Reserve'] as const),
  accountNumber: z.string().nullish(),
  bankName: z.string().nullish(),
});

// Export schema only, type is in accounts.ts

// Receivable schema (strict: principal is integer cents only; coerce Prisma wire shapes)
export const receivableSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    propertyId: z.string(),
    unitId: z.string().nullish().default(''),
    date: isoDateTimeLike,
    amountCents: intCents,
    status: z.enum([
      'Paid',
      'PartiallyPaid',
      'Processing',
      'Late',
      'Unpaid',
      'Pending',
      'Disputed',
      'WrittenOff',
    ] as const),
    type: z.string().nullish(),
    paymentMethod: z.enum(['Check', 'ACH', 'Credit Card', 'Cash', 'Money Order'] as const).nullish(),
    description: z.string().nullish(),
    receiptNumber: z.string().nullish(),
    leaseId: z.string().nullish(),
    journalEntryId: z.string().nullish(),
    rentObligationParty: z.enum(['TENANT', 'SUBSIDY_PHA'] as const).nullish(),
    idempotencyKey: z.string().nullish(),
    billingYear: z.number().int().nullish(),
    billingMonth: z.number().int().nullish(),
    lastProviderWebhookAt: z.string().nullish(),
    tenantName: z.string(),
    propertyName: z.string(),
    unitNumber: z.string(),
    disputeReason: z.string().nullish(),
    disputedAt: isoDateTimeLike.nullish(),
    isConcession: z.boolean().nullish(),
    unearnedPeriodStart: isoDateTimeLike.nullish(),
    unearnedPeriodEnd: isoDateTimeLike.nullish(),
    maintenanceTicketId: z.string().nullish(),
    accrualJournalEntryId: z.string().nullish(),
    cashJournalEntryId: z.string().nullish(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
    depositSettlementId: z.string().nullish(),
  })
  .strict();

// Export schema only, type is in transactions.ts

// GeneralLedgerEntry schema (integer cents; strict — no legacy debit/credit floats)
export const journalEntryLineSchema = z
  .object({
    accountId: z.string(),
    accountName: z.string(),
    debitCents: z.number().int().min(0),
    creditCents: z.number().int().min(0),
    debitCentsString: z.string().optional(),
    creditCentsString: z.string().optional(),
    id: z.string().optional(),
    date: z.string().optional(),
    cleared: z.boolean().optional(),
    description: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict()
  .refine((l) => (l.debitCents === 0) !== (l.creditCents === 0), {
    message: 'Each line must have exactly one of debitCents or creditCents zero (T-account line)',
    path: ['debitCents'],
  });

/** @deprecated Use `journalEntryLineSchema`; kept for gradual import renames */
export const generalLedgerEntrySchema = journalEntryLineSchema;

// JournalEntry schema
export const journalEntrySchema = z
  .object({
    id: z.string(),
    date: isoDateTimeLike,
    description: z.string(),
    propertyId: z.string(),
    referenceNumber: z.string().optional(),
    entries: z.array(journalEntryLineSchema),
    totalAmountCents: z.number().int().min(0),
    totalAmountCentsString: z.string().optional(),
    leaseId: z.string().optional(),
    workOrderId: z.string().optional(),
    receivableId: z.string().optional(),
    billId: z.string().optional(),
    basis: z.string().optional(),
    period: z.string().optional(),
  })
  .strict();

// Export schema only, type is in journal.ts

// ChartOfAccount schema
export const chartOfAccountSchema = z.object({
  id: z.string(),
  code: z.string().min(1, 'Account code is required'),
  name: z.string().min(1, 'Account name is required'),
  type: z.enum(['Asset', 'Liability', 'Equity', 'Income', 'Expense'] as const),
});

// Export schema only, type is in accounts.ts

// BankTransaction schema
const bankTransactionExplainabilitySchema = z.object({
  source: z.enum(['Plaid', 'Unknown']),
  matchStatus: z.enum(['matched', 'suggested', 'unmatched']),
  linkedEntity: z
    .object({
      type: z.enum(['StripePayout', 'Payment', 'JournalEntry']),
      id: z.string(),
    })
    .nullable(),
});

export const bankTransactionSchema = z.object({
  id: z.string(),
  bankAccountId: z.string().optional(),
  date: z.string(),
  description: z.string(),
  amount: z.number(),
  type: z.enum(['Deposit', 'Withdrawal', 'Fee', 'Transfer'] as const),
  matched: z.boolean(),
  matchConfidence: z.number().min(0).max(100).nullish(),
  paymentId: z.string().nullable().optional(),
  journalEntryId: z.string().nullable().optional(),
  stripePayoutId: z.string().nullable().optional(),
  plaidTransactionId: z.string().nullable().optional(),
  explainability: bankTransactionExplainabilitySchema.optional(),
});

// Export schema only, type is in transactions.ts

// Distribution schema
export const distributionSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  investorId: z.string().optional(),
  date: z.string(),
  /** Display dollars; API maps from persisted `amountCents` (DB is cents-only). */
  amount: z.number().finite(),
  /** Canonical integer cents (authoritative). */
  amountCents: z.number().int(),
  period: z.string().min(1, 'Period is required'),
  status: z.string(),
  propertyId: z.string().optional(),
  propertyName: z.string().optional(),
  acknowledgedAt: z.string().optional(),
  notes: z.string().optional(),
  stripeTransferId: z.string().optional(),
  stripeTransferStatus: z.string().optional(),
  stripeTransferFailureReason: z.string().optional(),
  lastProviderWebhookAt: z.string().optional(),
});

export type Distribution = z.infer<typeof distributionSchema>;

// Budget schema
export const budgetSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  year: z.number().int().positive(),
  revenue: z.number().nonnegative(),
  expenses: z.number().nonnegative(),
  netIncome: z.number(),
  occupancyTarget: z.number().min(0).max(100),
});

export type Budget = z.infer<typeof budgetSchema>;

// CAMData schema
export const camDataSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  category: z.string(),
  budget: z.number(),
  actual: z.number(),
  variance: z.number(),
  status: z.string(),
});

export type CAMData = z.infer<typeof camDataSchema>;
